import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Link, Typography,
} from '@mui/material';
import { signOut } from '../../app/auth/appAuth';
import { acceptTos, fetchTosStatus, TosApiError, type TosStatus } from '../../app/tos/tosApi';
import { resolveApiError } from '../../lib/i18nResolve';
import termsVersion from '../../legal/terms-version.json';

type GateState =
  | { kind: 'loading' }
  | { kind: 'ready'; status: TosStatus }
  | { kind: 'failed'; error: unknown };

function errorText(err: unknown, fallback: string): string {
  if (err instanceof TosApiError && err.payload?.error) return resolveApiError(err.payload) || fallback;
  return fallback;
}

function FullTerms({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, i18n } = useTranslation('terms');
  // The section/clause set is data, not a fixed key list, so read the bundle rather than naming a non-leaf key.
  const bundle = i18n.getResourceBundle(i18n.resolvedLanguage ?? 'en', 'terms') as { full: { sections: Record<string, Record<string, string>> } };
  const sections = bundle.full.sections;
  const ids = Object.keys(sections).sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));
  return (
    <Dialog open={open} onClose={onClose} scroll="paper" maxWidth="md" fullWidth aria-labelledby="tos-full-title" PaperProps={{ 'aria-modal': true } as object}>
      <DialogTitle id="tos-full-title">{t('full.title')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {t('draftNotice')} · {t('full.versionLine', { version: termsVersion.version })}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>{t('full.intro')}</Typography>
        {ids.map((id) => {
          const n = id.slice(1);
          const clauses = Object.keys(sections[id]).filter((k) => k !== 'title').sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));
          return (
            <Box key={id} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" component="h3">{n}. {sections[id].title}</Typography>
              {clauses.map((k) => (
                <Typography key={k} variant="body2" sx={{ mt: 0.5 }}>{n}.{k.slice(1)} {sections[id][k]}</Typography>
              ))}
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('popup.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * First-access Terms gate. Renders children only once GET /api/tos/status says the profile needs no
 * acceptance (`required:false`, which is also what the backend kill switch reports). Every other
 * state — loading, a failed read, an unaccepted profile — keeps the app unmounted, so nothing behind
 * the gate runs and hits a refused route. Accept is followed by a status REFETCH; the gate unblocks
 * only from that persisted answer, never from the accept response.
 */
export function TosGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('terms');
  const [state, setState] = React.useState<GateState>({ kind: 'loading' });
  const [agreed, setAgreed] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<unknown>(null);
  const [showFull, setShowFull] = React.useState(false);

  const load = React.useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      setState({ kind: 'ready', status: await fetchTosStatus() });
    } catch (error) {
      console.error('[TosGate] status read failed', error);
      setState({ kind: 'failed', error });
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const onContinue = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await acceptTos(termsVersion.version);
      await load();
    } catch (error) {
      console.error('[TosGate] accept failed', error);
      setSubmitError(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (state.kind === 'ready' && !state.status.required) return <>{children}</>;

  if (state.kind === 'loading') {
    return (
      <Box role="status" aria-label={t('popup.checking')} sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const failed = state.kind === 'failed';
  return (
    <>
      <Box sx={{ minHeight: '100vh' }} />
      <Dialog
        open
        disableEscapeKeyDown
        onClose={() => { /* blocking: backdrop click and Esc do not dismiss */ }}
        aria-labelledby="tos-title"
        aria-describedby="tos-body"
        PaperProps={{ 'aria-modal': true } as object}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="tos-title">{t('popup.title')}</DialogTitle>
        <DialogContent id="tos-body">
          {failed ? (
            <Alert severity="error" role="alert">{errorText(state.error, t('popup.statusFailed'))}</Alert>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>{t('draftNotice')}</Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}>{t('popup.bodyInternal', { version: termsVersion.version })}</Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}>{t('popup.keyLimits')}</Typography>
              <Link component="button" type="button" variant="body2" onClick={() => setShowFull(true)}>{t('popup.viewFull')}</Link>
              <Box sx={{ mt: 1.5 }}>
                <FormControlLabel
                  control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} required />}
                  label={t('popup.agree')}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block">{t('popup.blockedHint')}</Typography>
              {submitError != null && (
                <Alert severity="error" role="alert" sx={{ mt: 1.5 }}>{errorText(submitError, t('popup.acceptFailed'))}</Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { void signOut(); }}>{t('popup.signOut')}</Button>
          {failed ? (
            <Button variant="contained" onClick={() => { void load(); }}>{t('popup.retry')}</Button>
          ) : (
            <Button variant="contained" disabled={!agreed || submitting} onClick={() => { void onContinue(); }}>
              {t('popup.continue')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <FullTerms open={showFull} onClose={() => setShowFull(false)} />
    </>
  );
}
